import React, { useState, useEffect, useContext } from 'react';
import { Modal, Table, Button, Form, Spinner } from 'react-bootstrap';
import { FaWhatsapp, FaTrash, FaCopy } from 'react-icons/fa';
import { useTranslation } from "react-i18next";
import { toast } from 'react-toastify';
import { getAuthToken } from '../../util/auth';
import { getApiChara } from '../../util/getApi';
import AuthContext from '../../store/auth-context';

const UpcomingAppointmentsModal = ({ show, onHide, hospitalName }) => {
    const { t } = useTranslation();
    const authCtx = useContext(AuthContext);
    const [appointments, setAppointments] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [selectedDoctorId, setSelectedDoctorId] = useState("0");
    const [globalNote, setGlobalNote] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    // Initial Global Note Template with Placeholders
    useEffect(() => {
        if (show && hospitalName) {
            setGlobalNote(t("Hello {patient},\nReminder for your upcoming follow-up at {hospital}.\n\n*Appointment Details:*\nDate: {date}\nTime: {time}\nDoctor: Dr. {doctor}\n\nRegards,\n{hospital}", {
                patient: "{patient}",
                date: "{date}",
                time: "{time}",
                doctor: "{doctor}",
                hospital: hospitalName
            }));
        }
    }, [show, hospitalName]);

    // Fetch Doctors and Appointments
    useEffect(() => {
        if (show) {
            fetchDoctors();
            fetchAppointments();
        }
    }, [show]);

    const fetchDoctors = async () => {
        try {
            const token = getAuthToken();
            const apiChar = getApiChara();
            const response = await fetch(`${apiChar}/users/getDoctorsList`, {
                headers: { Authorization: "Bearer " + token }
            });
            if (response.ok) {
                const data = await response.json();
                setDoctors(data || []);
            }
        } catch (err) {
            console.error("Error fetching doctors:", err);
        }
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return "-";

        // Handle Array [YYYY, MM, DD]
        if (Array.isArray(dateValue)) {
            const [year, month, day] = dateValue;
            return `${day.toString().padStart(2, '0')}-${month.toString().padStart(2, '0')}-${year}`;
        }

        // Handle String YYYY-MM-DD
        if (typeof dateValue === 'string') {
            if (dateValue.includes('-')) {
                const parts = dateValue.split('-');
                if (parts.length === 3) {
                    return `${parts[2].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[0]}`;
                }
            }

            // Handle raw strings like 2026320
            const clean = dateValue.replace(/\D/g, '');
            if (clean.length >= 7) {
                const year = clean.substring(0, 4);
                let month, day;
                if (clean.length === 8) {
                    month = clean.substring(4, 6);
                    day = clean.substring(6, 8);
                } else if (clean.length === 7) {
                    month = clean.substring(4, 5);
                    day = clean.substring(5, 7);
                }
                return `${day.toString().padStart(2, '0')}-${month.toString().padStart(2, '0')}-${year}`;
            }
        }

        return dateValue.toString();
    };

    const fetchAppointments = async () => {
        setIsLoading(true);
        try {
            const token = getAuthToken();
            const apiChar = getApiChara();
            const response = await fetch(`${apiChar}/checkUp/getUpcomingCheckUpsByNextAppointment`, {
                headers: { Authorization: "Bearer " + token }
            });
            if (response.ok) {
                const data = await response.json();
                const mappedData = data.map(item => {
                    const patient = item.patientId || {};
                    const doctor = item.doctorId || {};

                    // Prioritize NextAppointment string if available, otherwise array
                    const nextApptValue = item.NextAppointment || item.nextAppointment || "-";
                    const formattedDateStr = formatDate(nextApptValue);

                    // Format time from dateTime string (e.g. "2025-09-15 16:52:11")
                    let apptTime = "-";
                    if (item.dateTime) {
                        try {
                            const timePart = item.dateTime.split(' ')[1]; // Get "16:52:11"
                            if (timePart) {
                                const [h, m] = timePart.split(':');
                                const hour = parseInt(h);
                                const ampm = hour >= 12 ? 'PM' : 'AM';
                                const displayHour = hour % 12 || 12;
                                apptTime = `${displayHour}:${m} ${ampm}`;
                            } else {
                                // Fallback to standard JS Date parsing if it's ISO
                                apptTime = new Date(item.dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            }
                        } catch (e) {
                            apptTime = "-";
                        }
                    }

                    const docName = `${doctor.firstName || ""} ${doctor.lastName || ""}`.trim() || "-";

                    return {
                        id: item.checkUpId,
                        name: `${patient.firstName || ""} ${patient.lastName || ""}`.trim(),
                        mobile: patient.mobile || "",
                        date: formattedDateStr,
                        time: apptTime,
                        doctorName: docName,
                        doctorId: doctor.userId,
                        hospital: hospitalName,
                        individualNote: ""
                    };
                });
                setAppointments(mappedData);
            }
        } catch (err) {
            console.error("Error fetching appointments:", err);
            toast.error(t("Error fetching upcoming appointments"));
        } finally {
            setIsLoading(false);
        }
    };

    const handleApplyGlobalNote = () => {
        if (!globalNote) {
            toast.warning(t("Please enter a global message first"));
            return;
        }

        setAppointments(prev => prev.map(appt => {
            let personalizedNote = globalNote
                .replace(/{patient}/g, appt.name)
                .replace(/{date}/g, appt.date)
                .replace(/{time}/g, appt.time)
                .replace(/{doctor}/g, appt.doctorName)
                .replace(/{hospital}/g, hospitalName);

            return {
                ...appt,
                individualNote: personalizedNote
            };
        }));
        toast.success(t("Personalized messages applied to all rows"));
    };

    const handleDelete = (id) => {
        setAppointments(prev => prev.filter(appt => appt.id !== id));
        toast.info(t("Removed from list"));
    };

    const handleIndividualNoteChange = (id, value) => {
        setAppointments(prev => prev.map(appt =>
            appt.id === id ? { ...appt, individualNote: value } : appt
        ));
    };

    const sendWhatsApp = (appt) => {
        const message = appt.individualNote || globalNote.replace(/{patient}/g, appt.name).replace(/{date}/g, appt.date).replace(/{time}/g, appt.time).replace(/{doctor}/g, appt.doctorName).replace(/{hospital}/g, hospitalName);

        const cleanMobile = appt.mobile.replace(/\D/g, '');
        if (!cleanMobile) {
            toast.error(t("Invalid mobile number"));
            return;
        }
        const encodedMsg = encodeURIComponent(message);
        const url = `https://wa.me/91${cleanMobile}?text=${encodedMsg}`;
        window.open(url, "_blank");
    };

    const filteredAppointments = selectedDoctorId === "0"
        ? appointments
        : appointments.filter(a => a.doctorId === Number(selectedDoctorId));

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable className="upcoming-modal">
            <style>
                {`
                .upcoming-modal .modal-header { border-bottom: 2px solid #f1f3f5; }
                .upcoming-modal .modal-content { border-radius: 12px; border: none; box-shadow: 0 10px 30px rgba(0,0,0,0.1); }
                .upcoming-modal .table thead th { background-color: #f8f9fa; border-top: none; font-size: 11px; }
                .upcoming-modal .note-field { font-size: 10px; padding: 6px 10px; border-radius: 6px; border: 1px solid #dee2e6 !important; min-height: 44px; }
                .upcoming-modal .text-theme { color: var(--primary-color) !important; }
                .upcoming-modal .bg-theme-subtle { background-color: rgba(0, 109, 119, 0.05) !important; border: 1px solid rgba(0, 109, 119, 0.1) !important; }
                .upcoming-modal .btn-theme { background-color: var(--primary-color) !important; border-color: var(--primary-color) !important; color: white !important; }
                .upcoming-modal .btn-theme:hover { background-color: var(--primary-hover) !important; }
                `}
            </style>
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="text-theme fw-bold">
                    <i className="fa fa-calendar-check-o me-2"></i>
                    {t("Upcoming Follow-ups")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                {/* Global Message and Filter Row */}
                <div className="row mb-4 bg-theme-subtle p-3 rounded-4 mx-0 g-3">
                    <div className="col-md-7">
                        <Form.Group>
                            <Form.Label className="fw-bold text-theme small text-uppercase mb-2">
                                <i className="fa fa-bullhorn me-1"></i> {t("Message Template (with placeholders)")}
                            </Form.Label>
                            <div className="d-flex gap-2">
                                <Form.Control
                                    as="textarea"
                                    rows={6}
                                    placeholder={t("Use {patient}, {date}, {time}, {doctor} as tags...")}
                                    value={globalNote}
                                    onChange={(e) => setGlobalNote(e.target.value)}
                                    className="border-primary-subtle font-monospace small"
                                    style={{ fontSize: '11px', lineHeight: '1.4' }}
                                />
                                <Button
                                    className="px-4 fw-bold text-nowrap d-flex align-items-center gap-2 btn-theme"
                                    onClick={handleApplyGlobalNote}
                                    title={t("Fill all patient notes using this template")}
                                >
                                    <FaCopy /> {t("Apply to All")}
                                </Button>
                            </div>
                        </Form.Group>
                    </div>
                    <div className="col-md-5">
                        <Form.Group>
                            <Form.Label className="fw-bold text-theme small text-uppercase mb-2">
                                <i className="fa fa-user-md me-1"></i> {t("Filter By Doctor")}
                            </Form.Label>
                            <Form.Select
                                value={selectedDoctorId}
                                onChange={(e) => setSelectedDoctorId(e.target.value)}
                                className="border-primary-subtle"
                                style={{ height: '44px' }}
                            >
                                <option value="0">{t("All Doctors")}</option>
                                {doctors.map(dr => (
                                    <option key={dr.userId} value={dr.userId}>{dr.firstName} {dr.lastName}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </div>
                </div>

                {isLoading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2 text-muted">{t("Loading upcoming follow-ups...")}</p>
                    </div>
                ) : (
                    <div className="table-responsive shadow-sm border rounded-4">
                        <Table hover className="mb-0 border-0">
                            <thead className="bg-light text-uppercase small fw-bold border-bottom">
                                <tr>
                                    <th className="py-3 text-center" style={{ width: '40px' }}>#</th>
                                    <th className="py-3" style={{ width: '150px' }}>{t("Patient Name")}</th>
                                    <th className="py-3" style={{ width: '110px' }}>{t("Mobile")}</th>
                                    <th className="py-3" style={{ width: '130px' }}>{t("Follow-up Date")}</th>
                                    <th className="py-3" style={{ width: '120px' }}>{t("Doctor")}</th>
                                    <th className="py-3">{t("Specific Message")}</th>
                                    <th className="py-3 text-center" style={{ width: '100px' }}>{t("Actions")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAppointments.length === 0 && (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5 text-muted">
                                            <i className="fa fa-info-circle fa-2x mb-2 d-block opacity-50"></i>
                                            {t("No upcoming follow-ups found.")}
                                        </td>
                                    </tr>
                                )}
                                {filteredAppointments.map((appt, index) => (
                                    <tr key={appt.id} className="align-middle border-bottom">
                                        <td className="text-center text-muted small">{index + 1}</td>
                                        <td>
                                            <div className="fw-bold text-dark small">{appt.name}</div>
                                        </td>
                                        <td><span className="text-theme font-monospace small">{appt.mobile}</span></td>
                                        <td>
                                            <div className="text-theme fw-bold small">
                                                {appt.date}<br />
                                                <span className="text-muted fw-normal">{appt.time}</span>
                                            </div>
                                        </td>
                                        <td className="small text-secondary">{appt.doctorName}</td>
                                        <td className="py-2">
                                            <Form.Control
                                                size="sm"
                                                as="textarea"
                                                rows={2}
                                                className="note-field bg-white"
                                                placeholder={t("Custom message...")}
                                                value={appt.individualNote}
                                                onChange={(e) => handleIndividualNoteChange(appt.id, e.target.value)}
                                            />
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex gap-2 justify-content-center">
                                                <Button
                                                    variant="success"
                                                    className="btn-sm rounded-3 d-flex align-items-center justify-content-center shadow-sm"
                                                    onClick={() => sendWhatsApp(appt)}
                                                    title={t("Send Individual WhatsApp")}
                                                    style={{ width: '32px', height: '32px' }}
                                                >
                                                    <FaWhatsapp />
                                                </Button>
                                                {/* <Button
                                                    variant="outline-danger"
                                                    className="btn-sm rounded-3 d-flex align-items-center justify-content-center shadow-sm"
                                                    onClick={() => handleDelete(appt.id)}
                                                    title={t("Remove from list")}
                                                    style={{ width: '32px', height: '32px' }}
                                                >
                                                    <FaTrash style={{ fontSize: '12px' }} />
                                                </Button> */}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default UpcomingAppointmentsModal;
